import { Component, OnInit, ViewChild } from "@angular/core";
import { LinkUserService, UserService } from "@main/users";
import { NotificationService } from "./notification.service";
import { Notification } from "./notification";
import { OverlayPanel } from "primeng/overlaypanel";
import { ConfirmationService, MessageService } from "primeng/api";
import { Message } from "@bbwt/classes";
import { Params, Router } from "@angular/router";
import { MarkAsReadRedirectNotifications, NotificationType, MarkAsReadRedirectOpenDialogNotifications } from "./notification-type";
import * as moment from "moment";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";
import { RedirectParam } from "./redirect-params";

@Component({
    selector: "notification-indicator",
    templateUrl: "./notification-indicator.component.html",
    styleUrls: ["./notification-indicator.component.css"]
})
export class NotificationIndicatorComponent implements OnInit {
    unarchivedNotifications: Notification[] = [];
    displayDialog: boolean;
    dialogNotification?: Notification;
    rowCountOnPage: number = 5;

    @ViewChild("panel", { static: false }) panel: OverlayPanel;

    constructor(
        private notificationService: NotificationService,
        public userService: UserService,
        private confirmationService: ConfirmationService,
        private messageService: MessageService,
        private router: Router,
        private broadcastService: BroadcastService,
        private patientService: PatientService,
        private linkedUserService: LinkUserService
    ) {
        this.notificationService.newNotifications.subscribe((result: Notification[]) => {
            this.unarchivedNotifications = result;
        });

        this.notificationService.getNotificationError.subscribe((error: string) => {
            this.messageService.add(Message.Error(error));
        });
    }

    async ngOnInit() {
        if (this.userService.isLogged) {
            this.initNotifications();
        }

        this.broadcastService.on(AppOnlineStateService.AppOnlineStateChanged).subscribe(() => {
            this.initNotifications();
        });
    }

    initNotifications() {
        if (this.userService.isLogged) {
            this.notificationService.checkHubConnection();
            this.notificationService.getAllNotifications().then(data => (this.unarchivedNotifications = data));
        }
    }

    get unarchivedAlerts(): Notification[] {
        return this.unarchivedNotifications;
    }

    markAllAsRead(event): void {
        this.confirmationService.confirm({
            message: "Are you sure that you want to mark all messages as read?",
            accept: () => {
                this.notificationService.markAllAsRead().then(_ => {
                    this.panel.hide();
                });
            }
        });
    }

    markAsRead(notification: Notification): void {
        this.notificationService.markAsRead(notification);
    }

    archive(notification: Notification): void {
        if (!notification.redirectTo) {
            this.notificationService.archive(notification.id).then(notification_ => {
                this.unarchivedNotifications = this.unarchivedNotifications.filter(x => x.id != notification_.id);
                if (this.unarchivedNotifications.length == 0) {
                    this.panel.hide();
                }
            });
        } else {
            this.redirect(notification);
        }
    }

    async onNotificationClicked(notification: Notification) {
        this.panel.hide();

        if (notification.type === NotificationType.FileShared) {
            this.markAsRead(notification);
            window.open(notification.redirectTo, "_blank");
        } else if (MarkAsReadRedirectNotifications.includes(notification.type)) {
            this.markAsRead(notification);
            if (MarkAsReadRedirectOpenDialogNotifications.includes(notification.type)) {
                this.openDialog(notification);
            }
            await this.changeOrganization(notification);
            this.redirect(notification);
        } else {
            this.openDialog(notification);
        }
    }

    private async changeOrganization(notification: Notification){
        const params = notification.redirectParams;

        const organizationId = params.find(p => p.name == "organizationId")?.value;
        const loginId = params.find(p => p.name == "loginId")?.value;
        if(organizationId && loginId){
            await this.linkedUserService.switchUser(loginId, organizationId);
            notification.redirectParams = notification.redirectParams.filter(p => p.name != "organizationId" && p.name != "loginId");
        }
    }

    private redirect(notification: Notification): void {
        const params = this.createParams(notification.redirectParams, notification);
        const queryParams = this.createParams<Params>(notification.queryParams, notification);

        this.router.navigate([notification.redirectTo, params], { queryParams });
    }

    private createParams<T = any>(params: RedirectParam[], notification: Notification) {
        return (params ?? []).reduce((prev, cur) => {
            this.setCurrentPatientFromParam(cur.name, cur.value, notification.type);
            prev[cur.name] = cur.value;
            return prev;
        }, <T>{});
    }

    private setCurrentPatientFromParam(name: string, value: string, notificationType?: NotificationType) {
        if (name === "patientId" || (name === "profileId" && notificationType === NotificationType.SelfGrantedOrganizationAccess)) {
            this.patientService.currentPatientId = value;
        }
    }

    private openDialog(notification: Notification) {
        this.dialogNotification = notification;
        this.displayDialog = true;
    }

    get unreadNotificationsCount(): number {
        return this.unarchivedNotifications.filter(notification => !notification.read).length;
    }

    getShortCreateDate(notification: Notification): string {
        return moment(notification.created).format("DD/MM/YYYY");
    }
}
